import { MessageDescriptor } from 'react-intl'

import { addMonths, differenceInYears, getYear, isBefore, parse, parseISO } from 'date-fns'
import { cloneDeep, isNil, isNumber, omit, sum, uniq } from 'lodash-es'
import moment from 'moment-timezone'

import {
  AgeBucket,
  ageBuckets,
  allChildPrimaryNeedsWordCloudMappings,
  ContactCareNavMessageSearcherType,
  ContactUsFormValues,
  currentToNextMetadataNamesAll,
  currentToNextMetadataNamesDefault,
  DirectPathBookingApplinkParams,
  PrimaryNeedsWords,
  SEARCHER_TYPE_TO_MATCH_FOR,
  SearcherForTriageSummary,
  TriageScreenerField,
  TriageScreenerValue,
  TriageWords,
} from './constants'
import { CustomerTreatmentOption, CustomerTreatmentsOptionLocation } from '../../models/customer/Customer'
import { ProgramNames } from '../../models/programs/Programs'
import {
  PROVIDER_TYPES,
  ProviderCapacity,
  ProviderInfo,
  ProviderType,
  TreatmentModeEnums,
  TreatmentNamesToProgramNamesInProviderCapacity,
} from '../../models/providers/Providers'
import {
  ExcludedReason,
  MetadataContext,
  SearcherType,
  SearchRequestData,
  TriageResults,
  TriageRiskLevel,
  TriageSearch,
  WordCloudCategory,
  WordCloudData,
} from '../../models/searchForCare/SearchForCare'
import { TriageScreenerNames, TriageScreener as TriageScreenerType } from '../../models/triageScreener/TriageScreener'
import { ContactPref, Customer, INTERNATIONAL_CONSENT_STATUS, User } from '../../models/user/User'
import {
  NeurodiversityContactCareNavigatorEntryPoint,
  NeurodiversitySearcherInfo,
  NeurodiversitySearcherType,
  TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC,
} from '../centerOfExcellence/types'
import { getToolkitSubTopicLabel } from '../centerOfExcellence/utils'
import { UnsupportedTreatmentOptionsConfigurationsType } from '../common/constants/baseTreatmentOptionConstants'
import { EligibilityType } from '../common/constants/constants'
import { CustomerName } from '../common/constants/customer'
import { PRODUCT_TYPE } from '../common/constants/productType'
import { DA_MEDS_PARAMS, MEDS_PARAMS } from '../common/constants/speculativeConstant'
import {
  tierOnePrimaryTreatmentOptionsToDisplay,
  tierOneSecondaryTreatmentOptionsToDisplay,
  TREATMENT_OPTIONS,
  TREATMENT_OPTIONS_TYPE,
} from '../common/constants/treatmentOptions'
import { TREATMENT_RECOMMENDATION_TIER } from '../common/constants/treatmentRecommendationsConstants'
import { isWithinPastTimeFrame, isWithinXHoursInThePast, TimeUnit } from '../common/utils/dateUtils'
import { isAgeTeen, isAgeYoungerThanTeen } from '../common/utils/userUtils'
import { S4C_PROGRAM_OPTION_FLOW } from '../memberPreferences/constants'
import { Programs } from '../programConfigs/programConfigs'
import { ProgramConfig } from '../programConfigs/types'
import {
  getToolkitSubTopicDescriptions,
  getToolkitTopicDescriptions,
  PRIMARY_AND_TRIAGE_WORDS_MAPPINGS_TO_TRANSLATION_MSG,
  PRIMARY_NEEDS_WORDS_ADULT_MSG,
  PRIMARY_NEEDS_WORDS_CHILD_MSG,
  TRIAGE_WORDS_MSG,
} from '../searchForCare/messages'
import { DEFAULT_SPECULATIVE_FILTER_THRESHOLDS } from '../wellnessToolkit/constants'
import { TOOLKIT_TYPE, WELLNESS_TOOLKIT_TYPE } from '../wellnessToolkit/types'

type AgeUtilArgs = {
  date: string
  ageArg?: number
  dateFormat?: string
}

export type UseNewSearchDataArgs = {
  dob?: string
  userId?: string
  gender?: string
  employer?: string
  type?: SearcherType
  childUserId?: string
  productType: PRODUCT_TYPE
  toolkitType?: TOOLKIT_TYPE
  toolkitSubTopic?: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC
  eligibilityType?: EligibilityType
  countryName?: string
}

type CheckProviderCapacityArgs = {
  providerCapacity?: ProviderCapacity
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE
  isDirectBooking?: boolean
  directBookingLimit?: string | null
}

const NUMBER_OF_MONTHS_UNTIL_SURVEY_CAN_BE_SHOWN_AGAIN = 3
const WELLNESS_SUB_TOPIC_SUFFIX = '-sub-topic'

export const getDirectBookingMedsParams = ({
  treatmentOption,
  isBlendedCareMedsAvailable,
}: {
  treatmentOption: TREATMENT_OPTIONS_TYPE
  isBlendedCareMedsAvailable: boolean
}): DirectPathBookingApplinkParams | undefined => {
  switch (treatmentOption) {
    case TREATMENT_OPTIONS.CONTROLLED_MEDICATION:
    case TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS:
      return DA_MEDS_PARAMS
    case TREATMENT_OPTIONS.MEDICATION_CONSULTATION:
    case TREATMENT_OPTIONS.BLENDED_CARE_MEDS:
      return isBlendedCareMedsAvailable ? MEDS_PARAMS : DA_MEDS_PARAMS
    default:
      return undefined
  }
}

// moment ignored the separator when parsing, so dash-separated DOBs (MM-DD-YYYY) must keep working.
const parseDob = (dob: string) => parse(dob.replace(/-/g, '/'), 'MM/dd/yyyy', new Date())

export const getAge = ({ date, dateFormat = 'MM/DD/YYYY' }: AgeUtilArgs) => {
  const momentDate = moment(date, dateFormat)
  if (!momentDate.isValid()) {
    return NaN
  }

  const age = moment().diff(moment(momentDate), 'years')
  return age
}

export function getAgeISO(iso8601Date: string): number {
  return differenceInYears(new Date(), parseISO(iso8601Date))
}

export const getAgeBucket = ({ date, dateFormat = 'MM/DD/YYYY', ageArg }: AgeUtilArgs): AgeBucket | undefined => {
  let age: number
  if (ageArg) {
    age = ageArg
  } else {
    age = getAge({ date, dateFormat })
  }
  return ageBuckets.find((ageBucket) => age >= ageBucket.min && age <= ageBucket.max)
}

export const getIsCareBeingSearchedForTeen = ({
  isOutsideOnboarding,
  isUserTeen,
  isSearcherTeen,
}: {
  isOutsideOnboarding: boolean
  isUserTeen: boolean
  isSearcherTeen: boolean
}) => {
  return isOutsideOnboarding ? isUserTeen : isSearcherTeen
}

export const getIsParentLedLctTeenBooking = ({
  isLyraCareProvider,
  isChildSearch,
  isSearcherTeen,
}: {
  isLyraCareProvider: boolean
  isChildSearch: boolean // true if a guardian is searching for care for a child.
  isSearcherTeen: boolean // true if care is being searched for a teen. Can be true if the user is a teen searching for self or if a guardian is searching for care for a child who is a teen.
}) => {
  return isLyraCareProvider && isChildSearch && isSearcherTeen
}

/** True when a parent is booking a DBT appointment for their teen. Gated on the DBT teens flag. (ACCOUNT-4311) */
export const getIsParentLedDbtTeenBooking = ({
  isDBTTeensEnabled,
  isDBTTeensProvider,
  isChildSearch,
}: {
  isDBTTeensEnabled: boolean
  isDBTTeensProvider: boolean
  isChildSearch: boolean
}) => {
  return isDBTTeensEnabled && isDBTTeensProvider && isChildSearch
}

export const getNewSearchData = ({
  dob,
  userId,
  gender,
  employer,
  type,
  childUserId,
  productType,
  toolkitType,
  toolkitSubTopic,
  eligibilityType,
  countryName,
}: UseNewSearchDataArgs) => {
  const age = dob ? getAge({ date: dob }) : null

  const yob = dob ? getYear(parseDob(dob)) : null
  const topicBody = toolkitType ? { clientSelectedTopic: getClientSelectedTopicKey(toolkitType) } : {}
  const subTopicBody =
    toolkitType && toolkitSubTopic
      ? {
          clientSelectedSubTopic: getClientSelectedSubTopicKey(toolkitSubTopic),
        }
      : {}
  const eligibilityTypeBody = eligibilityType ? { eligibility_type: eligibilityType } : {}

  if (!!userId && !isNil(age) && gender && employer && yob && type) {
    return {
      lyra_id: userId,
      body: {
        age,
        gender,
        employer,
        yob,
        type,
        child_lyra_id: childUserId,
        productType,
        ...topicBody,
        ...subTopicBody,
        ...eligibilityTypeBody,
        ...(countryName && {
          region: {
            value: countryName,
          },
        }),
      },
    }
  } else {
    return {}
  }
}

/**
 * Extracts AI-detected risk level from metadata_gen_ai_responses
 */
const getAiDetectedRiskLevel = (searchMetadataContext?: MetadataContext): TriageRiskLevel | undefined => {
  if (!searchMetadataContext) {
    return undefined
  }

  // Find any response with a highAlertRiskLevel
  const responseWithHighAlert = Object.values(searchMetadataContext).find(
    (response) => response?.gen_ai_response?.highAlertRiskLevel,
  )

  if (!responseWithHighAlert?.gen_ai_response?.highAlertRiskLevel) {
    return undefined
  }

  // Map server risk level format to TriageRiskLevel enum
  const riskLevelMap: { [key: string]: TriageRiskLevel } = {
    RISK_LEVEL_YELLOW: TriageRiskLevel.YELLOW,
    RISK_LEVEL_AMBER: TriageRiskLevel.AMBER,
    RISK_LEVEL_RED: TriageRiskLevel.RED,
  }

  return riskLevelMap[responseWithHighAlert.gen_ai_response.highAlertRiskLevel]
}

export const shouldShowHighRiskModal = (
  results: TriageResults,
  previousHighRiskModalLevelShown: TriageRiskLevel | undefined,
): boolean => {
  const traditionalRiskLevel = results.search?.risk_level || results.treatmentRecommendations?.riskLevel

  const aiRiskLevel = getAiDetectedRiskLevel(results.search?.metadata_context)

  const resultsRiskLevel = traditionalRiskLevel || aiRiskLevel

  if (!resultsRiskLevel) {
    return false
  }
  // show modal if first time presented
  if (!previousHighRiskModalLevelShown) {
    return true
  }
  // show modal if new risk level is higher than previous level
  return (
    previousHighRiskModalLevelShown === TriageRiskLevel.YELLOW &&
    (resultsRiskLevel === TriageRiskLevel.AMBER || resultsRiskLevel === TriageRiskLevel.RED)
  )
}

export const isActiveSI = (results: TriageResults) => {
  return (
    results.search?.suicidal_ideation_indicated || results.treatmentRecommendations?.suicidalIdeationIndicated || false
  )
}

/** Check for high risk searches and return the date of the most recent one, or null if none found.
 */
export const getMostRecentHighRiskSearchDateFromUserSearchHistory = ({ searches }: { searches: TriageSearch[] }) => {
  const highRiskSearch = searches.find(
    (search: TriageSearch) =>
      search.create_date &&
      (search.safety_alert_indicated || search.self_harm_indicated || search.suicidal_ideation_indicated),
  )
  if (highRiskSearch && highRiskSearch.create_date) return highRiskSearch.create_date
  return null
}

// Used to configure logic to bypass treatment recs page
// Returns a treatment option if skip conditions are met, this can be used to auto select a treatment option
export const getAutoSelectedTreatmentOption = (
  results: TriageResults,
  isCoachingOnly: boolean,
): TREATMENT_OPTIONS_TYPE | undefined => {
  let treatmentOption: TREATMENT_OPTIONS_TYPE | undefined = undefined
  const treatmentRecs = results.treatmentRecommendations
  if (!treatmentRecs) {
    return undefined
  }

  // Program aware triage rules
  const programAwareConditions =
    results.search?.body.programPreference !== undefined &&
    treatmentRecs.shownTierPage === TREATMENT_RECOMMENDATION_TIER.TIER_1

  if (
    (programAwareConditions || isCoachingOnly) &&
    treatmentRecs.primaryTreatmentOptions.filter((option) => tierOnePrimaryTreatmentOptionsToDisplay.includes(option))
      .length === 1 &&
    treatmentRecs.secondaryTreatmentOptions.filter((option) =>
      tierOneSecondaryTreatmentOptionsToDisplay.includes(option),
    ).length === 0 &&
    treatmentRecs.primaryTreatmentOptions[0] === TREATMENT_OPTIONS.COACHING
  ) {
    treatmentOption = TREATMENT_OPTIONS.COACHING
  }

  return treatmentOption
}

// Given a triage treatment option name, return the corresponding customer program.
// Note that we do not match treatment options to customer programs when the customer program is marked as "not a full
// treatment option". This means that the customer program is provided as part of another treatment option.
// An example of this is blended care therapy, which is provided as part of individual therapy. All blended care therapists
// are categorized under individual therapy, but not all therapists under individual therapy are part of blended care.
export const convertTriageTreatmentOptionToProgram = (
  treatmentOptionName: TREATMENT_OPTIONS_TYPE | null,
  allowPartialTreatmentOptions: boolean = false,
): ProgramConfig | undefined => {
  return Programs.getProgramConfigsMatching(
    (program) =>
      !!treatmentOptionName &&
      !!program.treatmentOptionProperties?.associatedTriageTreatmentOptions.includes(treatmentOptionName) &&
      (program.treatmentOptionProperties.isFullTreatmentOption || allowPartialTreatmentOptions),
  )[0]
}

// TODO: This should be consolidated with TreatmentNamesToProgramNamesInProviderCapacity and
// ProgramNamesInProviderCapacityToTreatmentNames
const getProgramNameFromSelectedTreatmentOptionForCapacity = (
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE,
): ProgramNames | 'DirectAccess' => {
  const matchingPrograms = Programs.getProgramConfigsMatching(
    (program) =>
      !!program.treatmentOptionProperties?.associatedTriageTreatmentOptions.includes(selectedTreatmentOption),
  )

  for (const program of matchingPrograms) {
    if (
      [ProgramNames.DIRECT_ACCESS_THERAPY, ProgramNames.ICASTherapy, ProgramNames.DIRECT_ACCESS_MEDS].includes(
        program.programName,
      )
    ) {
      return 'DirectAccess'
    }
  }

  return (
    (TreatmentNamesToProgramNamesInProviderCapacity[selectedTreatmentOption] as ProgramNames | undefined) ??
    matchingPrograms[0]?.programName ??
    ''
  )
}

// Takes the categorized word cloud elements returned by the server and flattens it
// so we can send it along in searches to provide context for which words the user saw.
export const formatWordCloudForSearchContext = (wordCloud?: WordCloudData) => {
  const wordList = wordCloud
    ? wordCloud.reduce((acc: any, value: WordCloudCategory) => {
        const defaultMessages = value.word_cloud_elements.map((element) => element.defaultMessage)
        return acc.concat(defaultMessages)
      }, [])
    : []

  return { displayed_wordclouds: wordList }
}
export const getProgramConfigByProvider = (provider: ProviderInfo): ProgramConfig | undefined => {
  const lyraType = provider?.lyra_type?.toUpperCase?.()
  if (!lyraType) {
    return undefined
  }
  const providerType: ProviderType | undefined = PROVIDER_TYPES[lyraType as keyof typeof PROVIDER_TYPES]
  return Programs.getProgramConfigByName(providerType?.program)
}

export const isWordCloud2Supported = (searcherType?: SearcherType, primaryNeed?: PrimaryNeedsWords) =>
  searcherType === SearcherType.ADULT ||
  (searcherType &&
    primaryNeed &&
    TriageScreenerNames.WORD_CLOUD_2 in
      ((allChildPrimaryNeedsWordCloudMappings[searcherType] as Partial<Record<PrimaryNeedsWords, object>>)[
        primaryNeed
      ] ?? {}))

export const getNumberOfScreeners = (searcherType?: SearcherType, primaryNeed?: PrimaryNeedsWords) => {
  const selectedCurrentToNextMetadataNames = searcherType
    ? currentToNextMetadataNamesAll[searcherType]
    : currentToNextMetadataNamesDefault
  const screenersWithoutWordCloud2 = omit(selectedCurrentToNextMetadataNames, ['wordCloud2'])
  // E.g. Younger children and middle children do not have wordCloud2.
  const includesWordCloud2 = searcherType && primaryNeed ? isWordCloud2Supported(searcherType, primaryNeed) : true

  return includesWordCloud2
    ? Object.entries(selectedCurrentToNextMetadataNames).length
    : Object.entries(screenersWithoutWordCloud2).length
}

export const isMinor = (dob: string) => (dob ? differenceInYears(new Date(), parseDob(dob)) < 18 : false)

export const isTreatmentOptionForAdultTherapy = (treatmentOption: TREATMENT_OPTIONS_TYPE | null) => {
  return (
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.COUPLES_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY
  )
}

export const isTreatmentOptionForChildTherapy = (treatmentOption: TREATMENT_OPTIONS_TYPE | null) => {
  return (
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD ||
    treatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY_CHILD
  )
}

// PROVREC-6142-specific Carelon scope; keep separate from the broader therapy classification used by other flows.
export const isCarelonOutpatientTherapyTreatmentOption = (treatmentOption: TREATMENT_OPTIONS_TYPE | null): boolean => {
  return (
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.COUPLES_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD ||
    treatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY_CHILD ||
    treatmentOption === TREATMENT_OPTIONS.BLENDED_CARE_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.ONSITE_THERAPY
  )
}

export type InternationalChildSearchArgs = {
  isUserInternational?: boolean
  // Age of the person who needs care (can be a child/dependent)
  searcherAge?: number
  matchFor?: SEARCHER_TYPE_TO_MATCH_FOR | string | null
}

export type InternationalChildTreatmentRecommendationsArgs = InternationalChildSearchArgs & {
  isOUSNeuroEnabled?: boolean
  hasAccessToInternationallySupportedTreatmentOptions?: boolean
  gdprInternationalConsent?: INTERNATIONAL_CONSENT_STATUS
}

export const isInternationalChildSearch = ({
  isUserInternational,
  searcherAge,
  matchFor,
}: InternationalChildSearchArgs): boolean =>
  !!isUserInternational &&
  ((isNumber(searcherAge) && isAgeYoungerThanTeen(searcherAge)) || matchFor === SEARCHER_TYPE_TO_MATCH_FOR.OTHER_CHILD)

/**
 * Child searches use the child's international consent, including None or a missing value.
 * Other searches use the guardian's consent.
 */
export const getSearchInternationalConsent = ({
  guardianConsent,
  childConsent,
  matchFor,
}: {
  guardianConsent?: INTERNATIONAL_CONSENT_STATUS
  childConsent?: INTERNATIONAL_CONSENT_STATUS
  matchFor?: SEARCHER_TYPE_TO_MATCH_FOR
}): INTERNATIONAL_CONSENT_STATUS | undefined =>
  matchFor === SEARCHER_TYPE_TO_MATCH_FOR.OTHER_CHILD ? childConsent : guardianConsent

/**
 * Returns the child ICAS card for international child searches without accepted consent or international program access.
 * Also returns the child ICAS card when OUS neuro is disabled.
 * Otherwise, returns `undefined` so callers apply the remaining recommendation rules.
 * The page and tracking use this helper to select the same recommendations.
 */
export const getInternationalChildTreatmentRecommendations = ({
  isUserInternational,
  searcherAge,
  matchFor,
  isOUSNeuroEnabled,
  hasAccessToInternationallySupportedTreatmentOptions,
  gdprInternationalConsent = INTERNATIONAL_CONSENT_STATUS.NONE,
}: InternationalChildTreatmentRecommendationsArgs):
  | {
      primaryTreatmentOptions: TREATMENT_OPTIONS_TYPE[]
      secondaryTreatmentOptions: TREATMENT_OPTIONS_TYPE[]
    }
  | undefined => {
  if (!isInternationalChildSearch({ isUserInternational, searcherAge, matchFor })) {
    return undefined
  }
  if (
    isOUSNeuroEnabled &&
    hasAccessToInternationallySupportedTreatmentOptions &&
    gdprInternationalConsent === INTERNATIONAL_CONSENT_STATUS.ACCEPTED
  ) {
    return undefined
  }
  return {
    primaryTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD_ICAS_PHONE],
    secondaryTreatmentOptions: [],
  }
}

// Checks if the treatment option is any type of therapy (adult, child, or specialized)
export const isTreatmentOptionTherapy = (treatmentOption: TREATMENT_OPTIONS_TYPE | null): boolean => {
  return (
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.COUPLES_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD ||
    treatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY_CHILD ||
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_ICAS_PHONE ||
    treatmentOption === TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD_ICAS_PHONE ||
    treatmentOption === TREATMENT_OPTIONS.BLENDED_CARE_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.ONSITE_THERAPY ||
    treatmentOption === TREATMENT_OPTIONS.CUSTOMER_PROVIDED_PROGRAM ||
    treatmentOption === TREATMENT_OPTIONS.ALCOHOL_USE_DISORDER
  )
}

// Checks if the treatment option is any type of coaching
export const isTreatmentOptionCoaching = (treatmentOption: TREATMENT_OPTIONS_TYPE | null) => {
  return (
    treatmentOption === TREATMENT_OPTIONS.COACHING ||
    treatmentOption === TREATMENT_OPTIONS.COACHING_FOR_PARENTS ||
    treatmentOption === TREATMENT_OPTIONS.LIVE_MESSAGING_COACHING ||
    treatmentOption === TREATMENT_OPTIONS.NEURODIVERSITY_SKILLS_COACHING ||
    treatmentOption === TREATMENT_OPTIONS.MANAGER_COACHING
  )
}

// Checks if the treatment option is any type of medication management
export const isTreatmentOptionMedicationManagement = (treatmentOption: TREATMENT_OPTIONS_TYPE | null) => {
  return (
    treatmentOption === TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS ||
    treatmentOption === TREATMENT_OPTIONS.BLENDED_CARE_MEDS ||
    treatmentOption === TREATMENT_OPTIONS.CONTROLLED_MEDICATION ||
    treatmentOption === TREATMENT_OPTIONS.MEDICATION_CONSULTATION ||
    treatmentOption === TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS_CHILD
  )
}

/**
 * Determine whether we should show the zero search results recommended treatment option page based on the treatment options.
 * Currently, only coaching is supported as a recommended treatment option.
 */
export const getHasCoachingAsEligibleTreatmentOption = ({
  treatmentOptions,
}: {
  treatmentOptions?: TREATMENT_OPTIONS_TYPE[]
}) => {
  if (!treatmentOptions) {
    return false
  }
  return treatmentOptions.some(isTreatmentOptionCoaching)
}

/**
 * Determines if the member preferences entry point section should be shown on provider results page
 * based on customer, user location, search path, and treatment option
 */
export const getShouldShowMemberPreferencesEntryPointSection = ({
  customerName,
  isUserInternational,
  isDirectPath,
  selectedTreatmentOption,
  isOnsiteFlow,
  outsideOnboarding,
}: {
  customerName: string
  isUserInternational: boolean
  isDirectPath: boolean
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE | null
  isOnsiteFlow: boolean
  outsideOnboarding: boolean
}): boolean => {
  if (isOnsiteFlow || outsideOnboarding) {
    return false
  }

  const isTherapyOrCoaching =
    isTreatmentOptionTherapy(selectedTreatmentOption) ||
    (isTreatmentOptionCoaching(selectedTreatmentOption) &&
      selectedTreatmentOption !== TREATMENT_OPTIONS.NEURODIVERSITY_SKILLS_COACHING)

  return !isUserInternational && customerName !== CustomerName.STARBUCKS && !isDirectPath && isTherapyOrCoaching
}

// Determines whether Carelon provider results should use the family/couples layout, which omits the preferences card
export const getShouldUseCarelonFamilyOrCouplesLayout = ({
  isCarelonOutpatientTherapyEnabled,
  selectedTreatmentOption,
}: {
  isCarelonOutpatientTherapyEnabled: boolean
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE | null
}): boolean => {
  return (
    isCarelonOutpatientTherapyEnabled &&
    (selectedTreatmentOption === TREATMENT_OPTIONS.COUPLES_THERAPY ||
      selectedTreatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY ||
      selectedTreatmentOption === TREATMENT_OPTIONS.FAMILY_THERAPY_CHILD)
  )
}

// Maps a child-search primary need to the care-option label used in the Salesforce case description
const getNeurodiversityPrimaryNeedLabel = (primaryNeed: PrimaryNeedsWords): string => {
  switch (primaryNeed) {
    case PrimaryNeedsWords.AUTISM_SPECTRUM:
      return 'Support for ASD'
    case PrimaryNeedsWords.ADHD:
      return 'Support for ADHD'
    case PrimaryNeedsWords.DEVELOPMENTAL_CONCERNS:
      return 'Developmental delays'
    default:
      return primaryNeed
  }
}

export const getContactCareNavMessageWithNeurodiversityInfo = ({
  contactCareNavigatorFormMessage,
  neurodiversitySearcherInfo,
  toolkitSubTopic,
  primaryNeed,
  childDetails,
}: {
  contactCareNavigatorFormMessage: string
  neurodiversitySearcherInfo: NeurodiversitySearcherInfo
  toolkitSubTopic?: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC
  primaryNeed?: PrimaryNeedsWords
  childDetails?: { name?: string; dob?: string; gender?: string }
}) => {
  const { contactCareNavEntryPoint, searcherType } = neurodiversitySearcherInfo
  const isChild = searcherType === NeurodiversitySearcherType.CHILD
  const searcherTypeString = `For ${
    isChild ? ContactCareNavMessageSearcherType.CHILD : ContactCareNavMessageSearcherType.SELF
  }`
  const getCaseInfo = () => {
    if (contactCareNavEntryPoint === NeurodiversityContactCareNavigatorEntryPoint.NEURODIVERSITY_NAVIGATION) {
      return 'Navigation/Care Advocacy'
    } else if (
      contactCareNavEntryPoint === NeurodiversityContactCareNavigatorEntryPoint.CHILD_SEARCH_FOR_CARE &&
      primaryNeed
    ) {
      return getNeurodiversityPrimaryNeedLabel(primaryNeed)
    } else if (contactCareNavEntryPoint === NeurodiversityContactCareNavigatorEntryPoint.SELECTED_CARE_OPTION) {
      return toolkitSubTopic ? getToolkitSubTopicLabel(toolkitSubTopic) : 'Support for neurodiversity needs'
    }
    return ''
  }
  // Child name/DOB/gender are only added for child searches; adult searches already carry these details on the case
  const childDetailsString =
    isChild && childDetails
      ? [
          childDetails.name && `Child Name: ${childDetails.name}`,
          childDetails.dob && `Child DOB: ${childDetails.dob}`,
          childDetails.gender && `Child Gender: ${childDetails.gender}`,
        ]
          .filter(Boolean)
          .join('\n')
      : ''
  const caseInfo = getCaseInfo()
  return `${searcherTypeString}${caseInfo ? ` - ${caseInfo}` : ''}${
    childDetailsString ? `\n\n${childDetailsString}` : ''
  }\n\n${contactCareNavigatorFormMessage}`
}

export const generatePostChildConcernContactPayload = ({
  user,
  customer,
  formValues,
  childLyraId,
  searchId,
  typeOfRequest,
}: {
  user: User
  customer: Customer
  formValues: ContactUsFormValues
  childLyraId?: string
  searchId?: string
  typeOfRequest?: string
}) => ({
  firstname: user?.firstname,
  lastname: user?.lastname,
  note: formValues.textareanote,
  careManagerName: customer?.defaultCareManager?.firstname || '',
  careManagerEmail: customer?.defaultCareManager?.username || '',
  call: formValues.phoneNumber,
  email: formValues.emailAddress || '',
  customerName: customer?.name || 'care',
  childLyraId,
  searchId,
  typeOfRequest,
})

export const generateContactUsFormValuesFromUserInfo = ({
  userContactPreference,
  userEmail,
  userPhone,
  contactCareNavigatorFormMessage,
}: {
  userContactPreference?: string
  userEmail?: string
  userPhone?: string | null
  contactCareNavigatorFormMessage?: string
}) => {
  const userPrefersEmail =
    isNil(userContactPreference) ||
    userContactPreference === ContactPref.UNASSIGNED ||
    userContactPreference === ContactPref.EMAIL
  const contactCareInfo: ContactUsFormValues = {
    emailChecked: userPrefersEmail,
    phoneChecked: !userPrefersEmail,
    emailAddress: !isNil(userEmail) ? userEmail : '',
    // don't include phone number if it isn't needed
    phoneNumber: !isNil(userPhone) && !userPrefersEmail ? userPhone : '',
    textareanote: '\n\n' + contactCareNavigatorFormMessage + '\n',
  }
  return contactCareInfo
}

export const getUnsupportedTreatmentOptionsConfiguration = ({
  unsupportedTreatmentOptionsConfigurations,
  excludedTreatmentOptionsReasons,
  isUserInternational,
}: {
  unsupportedTreatmentOptionsConfigurations: UnsupportedTreatmentOptionsConfigurationsType
  excludedTreatmentOptionsReasons?: ExcludedReason[]
  isUserInternational: boolean
}) => {
  if (isUserInternational || !excludedTreatmentOptionsReasons) {
    return null
  }
  const nonClinicalExclusionReasons = [
    'customerDisallowsSelfCareApps',
    'customerDisallowsStressManagement',
    'customerDisallowsStressManagementLM',
    'customerDisallowsSingleSessionCoaching',
    'customerDisallowsBCTherapy',
    'customerDisallowsDATherapy',
    'customerDisallowsBCMeds',
    'customerDisallowsDAMeds',
    'userBCMedsEligible', // When both BC and DA meds are eligible option, we remove DA meds
    'coachesUnavailable',
    'minorTreatmentTypeReplacedWithAdultType',
    'icasProvidersUnavailable', // excludes individualTherapy for eligible international users
    'icasProvidersAvailable', // excludes individualTherapyICASPhone for eligible international users
    'memberOUSAndNonEnglishDisplayLanguage', // excludes Coaching if member OUS and non-English display language
  ]

  const isExcludedForNonClinicalReasons = (optionToCheck: $TSFixMe) => {
    const excludedOption = excludedTreatmentOptionsReasons.find(
      (option: $TSFixMe) => option.treatmentOption === optionToCheck,
    )
    if (excludedOption) {
      const allReasonsAreNonClinical = excludedOption.reasons.every((reason: $TSFixMe) =>
        nonClinicalExclusionReasons.includes(reason),
      )
      const isNotAllowedByCustomer = excludedOption.reasons.some((reason: $TSFixMe) =>
        reason.startsWith('customerDisallows'),
      )
      return allReasonsAreNonClinical && isNotAllowedByCustomer
    } else {
      return false
    }
  }

  const medsDAChildExcluded = isExcludedForNonClinicalReasons(TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS_CHILD)
  const medsDAExcluded = isExcludedForNonClinicalReasons(TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS)
  const medsBCExcluded = isExcludedForNonClinicalReasons(TREATMENT_OPTIONS.BLENDED_CARE_MEDS)
  const individualTherapyChildExcluded = isExcludedForNonClinicalReasons(TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD)
  const individualTherapyExcluded = isExcludedForNonClinicalReasons(TREATMENT_OPTIONS.INDIVIDUAL_THERAPY)

  if (medsDAChildExcluded && medsDAExcluded && individualTherapyChildExcluded && individualTherapyExcluded) {
    return unsupportedTreatmentOptionsConfigurations.combined
  } else if (medsDAChildExcluded && medsDAExcluded) {
    return unsupportedTreatmentOptionsConfigurations.standalone.meds
  } else if (medsDAChildExcluded) {
    return unsupportedTreatmentOptionsConfigurations.standalone.medsChild
  } else if (medsDAExcluded && medsBCExcluded && individualTherapyExcluded) {
    return unsupportedTreatmentOptionsConfigurations.combined
  } else if (medsDAExcluded && medsBCExcluded) {
    return unsupportedTreatmentOptionsConfigurations.standalone.meds
  } else {
    return null
  }
}

// if the Child Search - Direct to CN feature flag is enabled, and
// the search is for child, then
// replace the PRIMARY_NEEDS metadata with the PRIMARY_NEEDS_DIRECT_CN metadata
export const usePrimaryNeedsDirectCNMetadata = (
  shouldShowChildDirectPathToCareNavigator: boolean,
  triageMetadata: TriageScreenerType[],
) => {
  let newMetadata = cloneDeep(triageMetadata)
  if (shouldShowChildDirectPathToCareNavigator) {
    const primaryNeedsID = newMetadata.find((metadata) => metadata.name === TriageScreenerNames.PRIMARY_NEEDS)
      ?.id as string
    newMetadata = newMetadata
      .filter((metadata) => metadata.name !== TriageScreenerNames.PRIMARY_NEEDS)
      .map((metadata) =>
        metadata.name === TriageScreenerNames.PRIMARY_NEEDS_DIRECT_CN
          ? {
              ...metadata,
              name: TriageScreenerNames.PRIMARY_NEEDS,
              id: primaryNeedsID,
            }
          : metadata,
      )
  }
  return newMetadata
}

/**
 * Internal function to check if a provider has capacity for any program.
 * Used in the direct booking case to ensure that providers are correctly shown as available.
 */
const checkProviderHasAnyCapacity = ({
  providerCapacity,
  isDirectBooking = false,
}: {
  providerCapacity?: ProviderCapacity
  isDirectBooking?: boolean
}) => {
  if (!isDirectBooking || !providerCapacity) {
    return false
  }

  return sum(Object.values(providerCapacity)) > 0
}

/**
 * Function to help check if a provider has capacity for a given treatment option.
 * Supports checking LCx and DA providers and handling legacy providers that do not have a providerCapacity object
 * @param directBookingLimit - Designates when a direct booking link was generated by a provider. We allow bookings if `limit` is present AND if it represents a time no more than 72 hours ago. This intentionally bypasses capacity to allow certain clients to book as vetted by the provider.

 */
export const checkProviderCapacity = ({
  providerCapacity,
  selectedTreatmentOption,
  isDirectBooking,
  directBookingLimit,
}: CheckProviderCapacityArgs) => {
  const legacyProviderCapacity = providerCapacity === undefined
  const program = getProgramNameFromSelectedTreatmentOptionForCapacity(selectedTreatmentOption)

  const hasCapacityForDirectBooking = checkProviderHasAnyCapacity({
    providerCapacity,
    isDirectBooking,
  })

  const hasCapacityForGivenProgram = providerCapacity?.[program] !== 0
  /**
   * Providers that do not have a providerCapacity object returned are legacy providers or bad
   * data providers that should not have been surfaced, but are being surfaced by Arbiter/PSS
   * These providers have a null value for providerCapacity in LyraTherapy UPS Database. On the front end we get undefined.
   * We should default these providers to always having capacity for backwards compatibility
   *
   * On Production we should always expect providerCapacity to be defined by provider results
   * and we should verify that the capacity value is non-zero for the program.
   */

  if (isDirectBooking) {
    let isLimitWithin72HoursInThePast = false
    if (directBookingLimit) {
      const limitInDate = new Date(atob(directBookingLimit))
      isLimitWithin72HoursInThePast = isWithinXHoursInThePast(limitInDate, 72)
    }
    return legacyProviderCapacity || hasCapacityForDirectBooking || isLimitWithin72HoursInThePast
  }
  return legacyProviderCapacity || hasCapacityForGivenProgram || hasCapacityForDirectBooking
}

export const isShowAppointmentsDisabled = ({
  providerCapacity,
  selectedTreatmentOption,
  hasAvailability,
  hasConnectedCalendar,
  hasCalendarError,
  isDirectBooking,
  providerMissingForSearch,
  directBookingLimit,
}: {
  providerCapacity?: ProviderCapacity
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE
  hasAvailability: boolean
  hasConnectedCalendar: boolean
  hasCalendarError?: boolean
  isDirectBooking?: boolean
  providerMissingForSearch?: boolean
} & Pick<CheckProviderCapacityArgs, 'directBookingLimit'>) => {
  const hasCapacity = checkProviderCapacity({
    providerCapacity,
    selectedTreatmentOption,
    isDirectBooking,
    directBookingLimit,
  })
  /**
   * A provider that does not have capacity or availability should not be allowed
   * to be booked.
   *
   * A provider with an error should not be allowed to be booked
   */
  return Boolean(
    (hasConnectedCalendar && (!hasCapacity || !hasAvailability || hasCalendarError)) || providerMissingForSearch,
  )
}

export const isContactForAvailabilityDisabled = ({
  providerCapacity,
  selectedTreatmentOption,
  isDirectBooking,
  directBookingLimit,
}: {
  providerCapacity?: ProviderCapacity
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE
  isDirectBooking?: boolean
} & Pick<CheckProviderCapacityArgs, 'directBookingLimit'>) => {
  const hasCapacity = checkProviderCapacity({
    providerCapacity,
    selectedTreatmentOption,
    isDirectBooking,
    directBookingLimit,
  })
  return !hasCapacity && !isDirectBooking
}

// Checks if a provider supports in-person appointments based on treatment mode
export const getProviderSupportsInPerson = ({ treatmentMode }: { treatmentMode: TreatmentModeEnums }): boolean => {
  const inPersonTreatments = [
    TreatmentModeEnums.ONLY_INPERSON,
    TreatmentModeEnums.BOTH_VIDEO_INPERSON,
    TreatmentModeEnums.INPERSON_PHONE_VIDEO,
    TreatmentModeEnums.BOTH_PHONE_INPERSON,
  ]
  return inPersonTreatments.some((inPersonTreatment) => inPersonTreatment === treatmentMode)
}

export const getInPersonProvidersCount = ({ providers }: { providers: ProviderInfo[] }) => {
  const inPersonProviderCount = providers.reduce((providerCount, currentProvider) => {
    const supportsInPerson = getProviderSupportsInPerson({
      treatmentMode: TreatmentModeEnums[currentProvider.supportedVisitTypes as keyof typeof TreatmentModeEnums],
    })

    return supportsInPerson ? providerCount + 1 : providerCount
  }, 0)

  return inPersonProviderCount
}

/**
 * Determines whether to show the zero in-person results header message.
 * Only applies to therapy searches (adult, teen, child) for US users.
 * @param providers - The list of providers from search results
 * @param isInPersonSearch - Whether the user searched for in-person providers
 * @param isInternational - Whether the user is an international user
 * @param selectedTreatmentOption - The treatment option selected during triage
 */
export const shouldShowZeroInPersonResultsHeader = ({
  providers,
  isInPersonSearch,
  isInternational,
  selectedTreatmentOption,
}: {
  providers: ProviderInfo[]
  isInPersonSearch: boolean
  isInternational: boolean
  selectedTreatmentOption?: TREATMENT_OPTIONS_TYPE | null
}): boolean => {
  if (isInternational) {
    return false
  }

  // Only show for therapy searches (adult, teen, child)
  if (!isTreatmentOptionTherapy(selectedTreatmentOption ?? null)) {
    return false
  }

  if (!isInPersonSearch) {
    return false
  }

  // Check if zero in-person providers were found
  const hasInPersonProviders = providers.some((provider) => provider.sessionFormat?.isInPerson)
  if (hasInPersonProviders) {
    return false
  }

  // Finally, confirm that there are at least one provider available
  return providers.length > 0
}

/**
 * Checks if the user is a teen searching for care between the ages of 13-17
 */
export const isSelfInitiatedTeenSearchingForCare = ({
  isSearchingForAdultTherapy,
  searcherDOB,
}: {
  isSearchingForAdultTherapy: boolean
  searcherDOB: string
}): boolean => {
  const age = getAge({ date: searcherDOB })
  return isSearchingForAdultTherapy && isAgeTeen(age)
}

/**
 * Returns current search for care flow of the user
 */
export const getSearchForCareProgramOptionFlow = ({
  isSearchingForAdultTherapy,
  isSearchingForCoaching,
  searcherDOB,
  isSearchingForChildTherapy,
  isLMSCoachingEligible,
}: {
  isSearchingForAdultTherapy: boolean
  isSearchingForCoaching: boolean
  searcherDOB: string
  isSearchingForChildTherapy: boolean
  isLMSCoachingEligible?: boolean
}): S4C_PROGRAM_OPTION_FLOW => {
  const isSelfInitiatedTeen = isSelfInitiatedTeenSearchingForCare({
    isSearchingForAdultTherapy,
    searcherDOB,
  })
  const isSeacherTeen = isAgeTeen(getAge({ date: searcherDOB }))
  if (isSearchingForChildTherapy && isSeacherTeen) {
    return S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_TEEN
  } else if (isSelfInitiatedTeen) {
    return S4C_PROGRAM_OPTION_FLOW.SELF_INITIATED_TEENS
  } else if (isSearchingForAdultTherapy) {
    return S4C_PROGRAM_OPTION_FLOW.THERAPY
  } else if (isLMSCoachingEligible && isSearchingForCoaching) {
    return S4C_PROGRAM_OPTION_FLOW.COACHING_WITH_LMS
  } else if (isSearchingForCoaching) {
    return S4C_PROGRAM_OPTION_FLOW.COACHING
  } else if (isSearchingForChildTherapy) {
    return S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_CHILD
  }

  return S4C_PROGRAM_OPTION_FLOW.OTHER
}

// The implicit in-person meeting style preference for parent-initiated child searches only
// applies to young children. Searches for children older than this do not get the implicit
// preference, though parents may still add it manually (PROVREC-6084).
const CHILD_IMPLICIT_IN_PERSON_PREFERENCE_MAX_AGE = 5

/**
 * Whether a parent-initiated child search should receive the implicit in-person meeting style
 * preference. True only for children aged 0-5; an invalid or missing DOB yields false so we
 * never over-apply the preference.
 */
export const shouldApplyImplicitInPersonPreferenceForChildSearch = ({
  searcherDOB,
}: {
  searcherDOB: string
}): boolean => {
  const age = getAge({ date: searcherDOB })
  return age >= 0 && age <= CHILD_IMPLICIT_IN_PERSON_PREFERENCE_MAX_AGE
}

export enum CHILD_SEARCH_IMPLICIT_IN_PERSON_PREFERENCE_ACTION {
  APPLY = 'APPLY',
  CLEAR = 'CLEAR',
  NONE = 'NONE',
}

/**
 * Determines how a parent-initiated child search should adjust the implicit in-person meeting
 * style preference on page load (PROVREC-6084):
 * - APPLY: children aged 0-5 with no saved meeting style preference receive the implicit in-person preference.
 * - CLEAR: children aged 6-12 with a saved in-person preference have it removed, since it was implicitly
 *   applied by an earlier child search (e.g. for a younger sibling). Explicit selections are protected by
 *   isParentInitiatedInPersonS4CPreferenceChecked, which flips to false once the user saves preferences
 *   during a child search. An invalid DOB never clears a saved preference — destructive adjustments
 *   require a real parsed age.
 * - NONE: no adjustment needed.
 */
export const getChildSearchImplicitInPersonPreferenceAction = ({
  isParentInitiatedChildSearch,
  searcherDOB,
  isParentInitiatedInPersonS4CPreferenceChecked,
  preferredAppointmentType,
}: {
  isParentInitiatedChildSearch: boolean
  searcherDOB: string
  isParentInitiatedInPersonS4CPreferenceChecked: boolean
  preferredAppointmentType: string[]
}): CHILD_SEARCH_IMPLICIT_IN_PERSON_PREFERENCE_ACTION => {
  if (!isParentInitiatedChildSearch || !isParentInitiatedInPersonS4CPreferenceChecked) {
    return CHILD_SEARCH_IMPLICIT_IN_PERSON_PREFERENCE_ACTION.NONE
  }
  if (shouldApplyImplicitInPersonPreferenceForChildSearch({ searcherDOB }) && preferredAppointmentType.length === 0) {
    return CHILD_SEARCH_IMPLICIT_IN_PERSON_PREFERENCE_ACTION.APPLY
  }
  // NaN (invalid DOB) fails this comparison, so bad data can never wipe a saved preference
  const isOlderThanImplicitInPersonBand = getAge({ date: searcherDOB }) > CHILD_IMPLICIT_IN_PERSON_PREFERENCE_MAX_AGE
  if (isOlderThanImplicitInPersonBand && preferredAppointmentType.length > 0) {
    return CHILD_SEARCH_IMPLICIT_IN_PERSON_PREFERENCE_ACTION.CLEAR
  }
  return CHILD_SEARCH_IMPLICIT_IN_PERSON_PREFERENCE_ACTION.NONE
}

export const getPrimaryNeedAndWordCloudExperiences = (search?: TriageSearch) => {
  const questionnaires = search?.body?.questionnaires
  const result = []
  if (questionnaires) {
    // Single select primaryNeed
    const whatsOnYourMindValue = (questionnaires as $TSFixMe)?.[TriageScreenerNames.PRIMARY_NEEDS]?.[
      TriageScreenerField.WHATS_ON_YOUR_MIND
    ]
    // Multiselect primaryNeed
    const selectionsValue = (questionnaires as $TSFixMe)?.[TriageScreenerNames.PRIMARY_NEEDS]?.[
      TriageScreenerField.SELECTIONS
    ]
    if (whatsOnYourMindValue) {
      result.push(whatsOnYourMindValue)
    }
    if (selectionsValue) {
      selectionsValue.forEach((value: string) => {
        result.push(value)
      })
    }
    if (
      (questionnaires as $TSFixMe)?.[TriageScreenerNames.WORD_CLOUD_1]?.[TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING]
    ) {
      result.push(
        (questionnaires as $TSFixMe)?.[TriageScreenerNames.WORD_CLOUD_1]?.[
          TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING
        ],
      )
    }
    if (
      (questionnaires as $TSFixMe)?.[TriageScreenerNames.WORD_CLOUD_2]?.[
        TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING_2
      ]
    ) {
      result.push(
        (questionnaires as $TSFixMe)?.[TriageScreenerNames.WORD_CLOUD_2]?.[
          TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING_2
        ],
      )
    }
  }
  if (search?.body.clientSelectedTopic) {
    if (search?.body.clientSelectedSubTopic) {
      result.push(search.body.clientSelectedSubTopic)
    } else {
      result.push(search.body.clientSelectedTopic)
    }
  }
  return uniq(result.flat().sort())
}

// Convert primary needs (adult and child) and triage words to their translated message
export const convertPrimaryAndTriageTermsToMessages = (word: string): MessageDescriptor | undefined => {
  // Try to use the mapping key first for a primary need and triage term then use the string as-is
  let mappingWord = word
  // Check if the word exists in the mapping dictionary
  if (word in PRIMARY_AND_TRIAGE_WORDS_MAPPINGS_TO_TRANSLATION_MSG) {
    mappingWord =
      PRIMARY_AND_TRIAGE_WORDS_MAPPINGS_TO_TRANSLATION_MSG[
        word as keyof typeof PRIMARY_AND_TRIAGE_WORDS_MAPPINGS_TO_TRANSLATION_MSG
      ]
  }
  // Check if mappingWord exists in any of the message dictionaries
  if (mappingWord in PRIMARY_NEEDS_WORDS_ADULT_MSG) {
    return PRIMARY_NEEDS_WORDS_ADULT_MSG[mappingWord as keyof typeof PRIMARY_NEEDS_WORDS_ADULT_MSG]
  }
  if (mappingWord in PRIMARY_NEEDS_WORDS_CHILD_MSG) {
    return PRIMARY_NEEDS_WORDS_CHILD_MSG[mappingWord as keyof typeof PRIMARY_NEEDS_WORDS_CHILD_MSG]
  }
  if (mappingWord in TRIAGE_WORDS_MSG) {
    return TRIAGE_WORDS_MSG[mappingWord as keyof typeof TRIAGE_WORDS_MSG]
  }
  const toolkitTopicDescriptionMsgs = getToolkitTopicDescriptions()
  const mappingWordTopic = getTopicFromClientSelectedTopicKey(mappingWord)
  if (mappingWordTopic in toolkitTopicDescriptionMsgs) {
    return toolkitTopicDescriptionMsgs[mappingWordTopic as keyof typeof toolkitTopicDescriptionMsgs]
  }
  const toolkitSubTopicDescriptionsMsgs = getToolkitSubTopicDescriptions()
  const mappingWordSubTopic = getTopicFromClientSelectedSubTopicKey(mappingWord)
  if (mappingWordSubTopic in toolkitSubTopicDescriptionsMsgs) {
    return toolkitSubTopicDescriptionsMsgs[mappingWordSubTopic as keyof typeof toolkitSubTopicDescriptionsMsgs]
  }

  return undefined
}

export const convertPrimaryAndTriageTermsToMessagesArr = (strArr: string[]): Array<MessageDescriptor> => {
  return strArr
    .map((word) => convertPrimaryAndTriageTermsToMessages(word))
    .filter((descriptor): descriptor is MessageDescriptor => descriptor !== undefined)
}

export const isChildSearch = (search: TriageSearch) => {
  return Boolean(search.body?.childLyraId || search.body?.child_lyra_id)
}

export const getTriageSummaryType = (age?: number, searcherType?: SearcherType): SearcherForTriageSummary => {
  if (isNumber(age) && !isNil(SearcherForTriageSummary)) {
    if (isAgeYoungerThanTeen(age)) {
      return SearcherForTriageSummary.CHILD
    } else if (isAgeTeen(age) && searcherType !== SearcherType.ADULT) {
      return SearcherForTriageSummary.TEEN
    } else {
      return SearcherForTriageSummary.ADULT
    }
  } else {
    return SearcherForTriageSummary.ADULT
  }
}

export const getSpeculativeSearchDefaultCoachingSearchParams = ({
  isInternationalUser,
  userCountryName,
  userDOB,
  isSpeculativeFilterEnabled,
}: {
  isInternationalUser: boolean
  userCountryName?: string
  userDOB?: string
  isSpeculativeFilterEnabled: boolean
}) => {
  const defaultSearchParams: SearchRequestData = {
    isUserInternational: isInternationalUser,
    onsite: false,
    matchFor: 'self',
    locationCountry: userCountryName,
    treatmentNaive: TREATMENT_OPTIONS.COACHING,
    treatmentPreference: TREATMENT_OPTIONS.COACHING,
    ...(isSpeculativeFilterEnabled && {
      ...DEFAULT_SPECULATIVE_FILTER_THRESHOLDS[WELLNESS_TOOLKIT_TYPE.INDIVIDUAL_COACHING],
    }),
  }

  defaultSearchParams.age = getAge({ date: userDOB ?? '' })

  return defaultSearchParams
}

export const getShowProviderResultsSurveyUsingDateLastSeen = ({
  providerResultsSurveyDateLastSeen,
}: {
  providerResultsSurveyDateLastSeen?: string
}) => {
  let showSurvey = false

  if (providerResultsSurveyDateLastSeen) {
    const dateThreeMonthsAhead = addMonths(
      parseISO(providerResultsSurveyDateLastSeen),
      NUMBER_OF_MONTHS_UNTIL_SURVEY_CAN_BE_SHOWN_AGAIN,
    )
    if (isBefore(new Date(), dateThreeMonthsAhead)) {
      showSurvey = false
    } else {
      showSurvey = true
    }
  } else {
    showSurvey = true
  }

  return showSurvey
}

/** Append 'topic' at the end of the key to make sure the value doesn't conflict with other keys in the triage flow. For example if the stress toolkit key was 'stress' it can conflict with the 'stress' word cloud option */
export const getClientSelectedTopicKey = (topic?: string) => {
  if (!topic) {
    return ''
  }
  return `${topic}-topic`
}

const getTopicFromClientSelectedTopicKey = (key: string) => {
  if (key.endsWith('-topic')) {
    return key.slice(0, -6)
  }
  return key
}

export const getClientSelectedSubTopicKey = (subTopic?: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC): string => {
  if (!subTopic) {
    return ''
  }
  return `${subTopic}${WELLNESS_SUB_TOPIC_SUFFIX}`
}

const getTopicFromClientSelectedSubTopicKey = (key: string) => {
  if (key.endsWith(WELLNESS_SUB_TOPIC_SUFFIX)) {
    return key.replace(WELLNESS_SUB_TOPIC_SUFFIX, '')
  }
  return key
}

export const shouldSkipPrimaryNeeds = ({
  isSearchProgramAware,
  isSearchToolkitAware,
  isCOEFlow,
}: {
  isSearchProgramAware?: boolean
  isSearchToolkitAware?: boolean
  isCOEFlow: boolean
}) => {
  return isSearchToolkitAware || isSearchProgramAware || isCOEFlow ? TriageScreenerNames.PRIMARY_NEEDS : undefined
}

/**
 * Mapping of GenAI dynamic variables to their corresponding data sources
 */
export interface GenAiVariableData {
  employer?: string
  client_first_name?: string
  toll_free_number?: string
  emergency_phone_number?: string
}

/**
 * Replaces dynamic variables in GenAI response text with actual user/customer data.
 * Dynamic variables are in the format {{variableName}}
 * Example:
 * - Input: "Lyra is a benefit provided to you by {{employer}}"
 * - Output: "Lyra is a benefit provided to you by eBay"
 * @param text - The GenAI response text containing dynamic variables
 * @param data - Object containing the variable values to inject
 * @returns The text with all dynamic variables replaced with actual values
 */
export const injectGenAiVariables = (
  text: string | null | undefined,
  data: GenAiVariableData,
): string | null | undefined => {
  if (!text) {
    return text
  }

  // Replace all {{variableName}} with the corresponding value from data
  return text.replaceAll(/\{\{(\w+)\}\}/g, (match, variableName) => {
    const value = data[variableName as keyof GenAiVariableData]
    return value !== undefined ? value : match
  })
}

export interface SkipGenAIParams {
  submittedMetadataName: string
  displayMetadataName?: string
  submittedValues: Record<string, any>
  allQuestionnaires: Record<string, any>
  highAlertRiskLevel?: string
}

export const shouldSkipGenAI = ({
  submittedMetadataName,
  submittedValues,
  allQuestionnaires,
  highAlertRiskLevel,
}: SkipGenAIParams): boolean => {
  // Skip GenAI if high alert risk level is detected in AI freetext
  if (highAlertRiskLevel) {
    return true
  }
  // Use submittedValues for the screener that was just submitted, otherwise use stored data
  const primaryNeedsData =
    submittedMetadataName === TriageScreenerNames.PRIMARY_NEEDS
      ? submittedValues
      : allQuestionnaires[TriageScreenerNames.PRIMARY_NEEDS] || {}
  const wordCloud1Data =
    submittedMetadataName === TriageScreenerNames.WORD_CLOUD_1
      ? submittedValues
      : allQuestionnaires[TriageScreenerNames.WORD_CLOUD_1] || {}
  const wordCloud2Data =
    submittedMetadataName === TriageScreenerNames.WORD_CLOUD_2
      ? submittedValues
      : allQuestionnaires[TriageScreenerNames.WORD_CLOUD_2] || {}
  const intensityData =
    submittedMetadataName === TriageScreenerNames.INTENSITY
      ? submittedValues
      : allQuestionnaires[TriageScreenerNames.INTENSITY] || {}

  const selections = primaryNeedsData[TriageScreenerField.SELECTIONS] || []
  const whatsOnYourMind = primaryNeedsData[TriageScreenerField.WHATS_ON_YOUR_MIND]
  const whatElseOnYourMind = primaryNeedsData[TriageScreenerField.WHAT_ELSE_ON_YOUR_MIND]
  const whatAreYouExperiencing = wordCloud1Data[TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING] || []
  const whatAreYouExperiencing2 = wordCloud2Data[TriageScreenerField.WHAT_ARE_YOU_EXPERIENCING_2] || []
  const selfHarmSliderValue = intensityData[TriageWords.SELF_HARM]

  // Skip GenAI if no primary needs selected or only "Something else" without free text
  if (submittedMetadataName === TriageScreenerNames.PRIMARY_NEEDS) {
    const hasNoFreeText = !whatElseOnYourMind || whatElseOnYourMind.trim() === ''
    const hasNoOtherPrimaryNeeds = Array.isArray(selections)
      ? selections.filter((s) => s !== TriageScreenerValue.SOMETHING_ELSE).length === 0
      : whatsOnYourMind === TriageScreenerValue.SOMETHING_ELSE || !whatsOnYourMind

    if (hasNoFreeText && hasNoOtherPrimaryNeeds) {
      return true
    }
  }

  // Skip GenAI if no word cloud topics selected or if "Self-harm" is selected
  if (submittedMetadataName === TriageScreenerNames.WORD_CLOUD_1) {
    const selectionsArray = (
      Array.isArray(whatAreYouExperiencing) ? whatAreYouExperiencing : [whatAreYouExperiencing].filter(Boolean)
    ) as string[]

    if (selectionsArray.length === 0) {
      return true
    }

    if (selectionsArray.includes(TriageWords.SELF_HARM)) {
      return true
    }
  }

  if (submittedMetadataName === TriageScreenerNames.WORD_CLOUD_2) {
    const wc1Selections = (
      Array.isArray(whatAreYouExperiencing) ? whatAreYouExperiencing : [whatAreYouExperiencing].filter(Boolean)
    ) as string[]
    const wc2Selections = (
      Array.isArray(whatAreYouExperiencing2) ? whatAreYouExperiencing2 : [whatAreYouExperiencing2].filter(Boolean)
    ) as string[]
    const combinedSelections = [...wc1Selections, ...wc2Selections]

    if (combinedSelections.length === 0) {
      return true
    }

    if (combinedSelections.includes(TriageWords.SELF_HARM)) {
      return true
    }
  }

  // Skip GenAI if "Self-harm" slider submitted or only nonSpecifiedPrimaryNeed slider present
  if (submittedMetadataName === TriageScreenerNames.INTENSITY) {
    const intensityKeys = Object.keys(intensityData || {})
    const sliderKeys = intensityKeys.filter((key) => key !== 'metadataId')
    const hasOnlyNonSpecifiedPrimaryNeedSlider =
      sliderKeys.length === 1 && sliderKeys[0] === TriageScreenerField.NON_SPECIFIED_PRIMARY_NEED

    if (hasOnlyNonSpecifiedPrimaryNeedSlider) {
      return true
    }

    if (selfHarmSliderValue !== undefined && selfHarmSliderValue !== null) {
      return true
    }
  }

  return false
}

export const getTriageSearchesWithinPastTimeFrame = (
  triageSearches: TriageSearch[],
  amount: number,
  unit: TimeUnit,
) => {
  return triageSearches.filter((search) => {
    if (!search.update_date) return false
    const searchDate = new Date(search.update_date)
    return isWithinPastTimeFrame(searchDate, amount, unit)
  })
}

/**
 * Normalize employee group to handle variations in case, whitespace, commas, and dash types.
 */
export const normalizeEmployeeGroup = (value: string): string =>
  value
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/,/g, '')
    .replace(/[-–—]/g, '')
    .trim()

/**
 * Checks whether a customer treatment option is eligible based on age requirements,
 * ranking for the given location, and display rules. Shared by useAddCustomerTreatmentOptions
 * and getEligibleOtherTreatmentNames.
 */
export const isCustomerTreatmentOptionEligible = ({
  item,
  location,
  searcherAge,
  actingSearcherAge,
  tier,
  eligibleTreatmentOptions,
  employeeGroup,
}: {
  item: CustomerTreatmentOption
  location: CustomerTreatmentsOptionLocation
  searcherAge: number | null | undefined
  actingSearcherAge: number | null | undefined
  tier?: TREATMENT_RECOMMENDATION_TIER
  eligibleTreatmentOptions?: string[]
  employeeGroup?: string
}): boolean => {
  const hasValidRanking = item.optionRanking[location] !== undefined
  const meetsSearcherAgeReq = item.config?.minAge == null || (searcherAge != null && searcherAge >= item.config.minAge)
  const meetsActingSearcherAgeReq =
    item.config?.actingSearcherMinAge == null ||
    (actingSearcherAge != null && actingSearcherAge >= item.config.actingSearcherMinAge)

  if (!hasValidRanking || !meetsSearcherAgeReq || !meetsActingSearcherAgeReq) {
    return false
  }

  if (!item.displayRules) {
    return true
  }

  const tierCheckPasses =
    !item.displayRules.showForTiers?.length || (tier != null && item.displayRules.showForTiers.includes(tier))
  const treatmentOptionsCheckPasses =
    !item.displayRules.showForTreatmentOptions?.length ||
    item.displayRules.showForTreatmentOptions.some((option) => (eligibleTreatmentOptions ?? []).includes(option))
  const employeeGroupCheckPasses =
    !item.displayRules.eligibility?.employeeGroup?.length ||
    (employeeGroup != null &&
      item.displayRules.eligibility.employeeGroup.some(
        (group) => normalizeEmployeeGroup(employeeGroup) === normalizeEmployeeGroup(group),
      ))

  return tierCheckPasses && treatmentOptionsCheckPasses && employeeGroupCheckPasses
}

/**
 * Returns analytics names for customer-specific treatment options eligible for
 * the given tier page, searcher age, acting searcher age, and display rules.
 */
export const getEligibleOtherTreatmentNames = ({
  treatmentOptions,
  shownTierPage,
  searcherAge,
  userAge,
  eligibleTreatmentOptions,
  employeeGroup,
}: {
  treatmentOptions?: CustomerTreatmentOption[]
  shownTierPage?: TREATMENT_RECOMMENDATION_TIER
  searcherAge?: number | null
  userAge?: number | null
  eligibleTreatmentOptions?: string[]
  employeeGroup?: string
}): string[] | undefined => {
  if (shownTierPage == null) {
    return undefined
  }
  const names = treatmentOptions
    ?.filter(
      (item) =>
        item.config.customCopy?.analyticsName &&
        isCustomerTreatmentOptionEligible({
          item,
          location: shownTierPage,
          searcherAge,
          actingSearcherAge: userAge,
          tier: shownTierPage,
          eligibleTreatmentOptions,
          employeeGroup,
        }),
    )
    .map(({ config }) => config.customCopy!.analyticsName!)
  return names?.length ? names : undefined
}
